import type { Meeting } from "@scoach/types";
import { Brain } from "@scoach/ui/icons";
import { useState } from "react";

import { ErrorBoundary } from "../../../../components/ErrorBoundary.tsx";
import { CompanyReportPanel } from "../../../companies/CompanyReportPanel.tsx";
import { LiveContextPanel } from "./LiveContextPanel.tsx";
import { LivePluginsPanel } from "./LivePluginsPanel.tsx";
import { NotesPanel } from "./NotesPanel.tsx";
import { ScreenSharePreview, type ScreenSharePreviewProps } from "./ScreenSharePreview.tsx";
import { SentimentDual } from "./SentimentDual.tsx";

export interface ContextRailProps extends Partial<ScreenSharePreviewProps> {
  meeting: Meeting;
  isSimulation?: boolean;
}

function SimulationCard({ meeting }: { meeting: Meeting }) {
  const contact = meeting.participants.find((p) => p.side === "client");
  return (
    <section className="share-card" style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Brain size={16} />
        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-1)" }}>
          Simulation Mode
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
        <div>
          <strong>Client:</strong> {meeting.account.name || "Unknown"}
          {meeting.account.industry && ` (${meeting.account.industry})`}
        </div>
        {contact && (
          <div>
            <strong>Contact:</strong> {contact.name}
            {contact.role && ` — ${contact.role}`}
          </div>
        )}
        <div>
          <strong>Stage:</strong> {meeting.stage}
        </div>
        {meeting.goal && (
          <div>
            <strong>Goal:</strong> {meeting.goal}
          </div>
        )}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-4)" }}>
        Gemini Live is roleplaying as the client. Speak naturally — hints and coaching are live.
      </div>
    </section>
  );
}

/** Company intel + Plugins as tabs in one card (Plugins is an extra tab next to
 * Company intel). Intel tab appears only when the meeting has a company; the
 * Plugins tab is always available. */
function IntelPluginsCard({ meeting }: { meeting: Meeting }) {
  const hasCompany = Boolean(meeting.account?.isInternal || meeting.account?.name);
  const [tab, setTab] = useState<"intel" | "plugins">(hasCompany ? "intel" : "plugins");

  return (
    <section className="share-card" style={{ padding: 14 }}>
      <div className="seg seg-sm" style={{ marginBottom: 10 }}>
        {hasCompany && (
          <button
            type="button"
            className={tab === "intel" ? "on" : ""}
            onClick={() => setTab("intel")}
          >
            Company intel
          </button>
        )}
        <button
          type="button"
          className={tab === "plugins" ? "on" : ""}
          onClick={() => setTab("plugins")}
        >
          Plugins
        </button>
      </div>

      {tab === "intel" && hasCompany ? (
        <ErrorBoundary label="live-company-intel">
          {meeting.account?.isInternal ? (
            <CompanyReportPanel companyName="" searchable />
          ) : (
            <CompanyReportPanel companyName={meeting.account.name} />
          )}
        </ErrorBoundary>
      ) : (
        <ErrorBoundary label="live-plugins">
          <LivePluginsPanel meetingId={meeting.id} />
        </ErrorBoundary>
      )}
    </section>
  );
}

export function ContextRail({
  meeting,
  isSimulation,
  stream,
  rmsDb,
  error,
  onPickSource,
  onStopShare,
  micCapturing,
  micError,
  listening,
}: ContextRailProps) {
  return (
    <aside className="rail">
      {isSimulation ? (
        <SimulationCard meeting={meeting} />
      ) : (
        <ScreenSharePreview
          stream={stream ?? null}
          rmsDb={rmsDb ?? Number.NEGATIVE_INFINITY}
          error={error ?? null}
          onPickSource={onPickSource ?? (() => {})}
          onStopShare={onStopShare}
          micCapturing={micCapturing}
          micError={micError}
          meetingId={meeting.id}
          listening={listening}
        />
      )}

      <SentimentDual />

      <NotesPanel meetingId={meeting.id} />

      {/* Context docs: attach a file or a Google Drive link mid-call so hints can
          reference its content (refreshes the live session within one cycle). */}
      {!isSimulation && (
        <ErrorBoundary label="live-context-docs">
          <LiveContextPanel meetingId={meeting.id} initialFiles={meeting.contextFiles ?? []} />
        </ErrorBoundary>
      )}

      {/* Company intel + Plugins in one card: Plugins is an extra tab right next to
          Company intel. External meetings auto-load the customer's intel; internal
          meetings show a search box. The Plugins tab renders the user's enabled,
          approved live-surface plugins (sandboxed PluginHost with scoped context). */}
      {!isSimulation && <IntelPluginsCard meeting={meeting} />}
    </aside>
  );
}
